export interface OrgRouteLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

export const GRAFANA_EXPLORER_ROUTE = '/app/grafana';

export const isGrafanaExplorerPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === GRAFANA_EXPLORER_ROUTE;

const ORG_SCOPED_DETAIL_ROUTES: Array<{ pattern: RegExp; fallbackPath: string }> = [
  { pattern: /^\/app\/incidents\/[^/]+(?:\/.*)?$/, fallbackPath: '/app/incidents' },
  { pattern: /^\/app\/incidents-v2\/[^/]+(?:\/.*)?$/, fallbackPath: '/app/incidents' },
  { pattern: /^\/app\/inventory\/[^/]+(?:\/.*)?$/, fallbackPath: '/app/inventory' },
  { pattern: /^\/app\/runbooks\/executions\/[^/]+(?:\/.*)?$/, fallbackPath: '/app/runbooks/executions' },
  { pattern: /^\/app\/runbooks\/(?!schedules(?:\/|$)|executions(?:\/|$)|library(?:\/|$)|new(?:\/|$))[^/]+(?:\/.*)?$/, fallbackPath: '/app/runbooks/library' }
];

export const isOrgScopedPath = (pathname: string): boolean => pathname === '/app' || pathname.startsWith('/app/');

export const buildOrgScopedPath = (
  pathname: string,
  orgId: string,
  search?: string | URLSearchParams,
  hash = ''
): string => {
  const params = search instanceof URLSearchParams ? new URLSearchParams(search) : new URLSearchParams(search ?? '');
  params.set('orgId', orgId);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
};

export const canonicalizeOrgLocation = (location: OrgRouteLocation, orgId: string): OrgRouteLocation => {
  const params = new URLSearchParams(location.search ?? '');
  params.set('orgId', orgId);
  const search = params.toString();
  return {
    pathname: location.pathname === '/app' ? '/app/overview' : location.pathname,
    search: search ? `?${search}` : '',
    hash: location.hash ?? ''
  };
};

/**
 * Manual organization changes intentionally discard context owned by the old
 * organization. Route-driven changes (deep links) must not use this cleanup.
 */
export const buildManualOrgSwitchLocation = (
  location: OrgRouteLocation,
  targetOrgId: string
): OrgRouteLocation => {
  const pathname = ORG_SCOPED_DETAIL_ROUTES.find(({ pattern }) => pattern.test(location.pathname))?.fallbackPath ?? location.pathname;
  const currentParams = new URLSearchParams(location.search ?? '');
  const nextParams = new URLSearchParams();
  nextParams.set('orgId', targetOrgId);
  if (currentParams.get('embed') === '1') {
    nextParams.set('embed', '1');
  }
  return {
    pathname,
    search: `?${nextParams.toString()}`,
    hash: ''
  };
};
